// Automatic statement reader. No UI, no libraries — takes raw rows (arrays of
// cells, as produced by Papa/XLSX) or plain text lines (PDF / pasted) and
// works out for itself which column is the date, which is the amount, which is
// the description, whether money out is negative or positive, and what the
// date format is. Nothing here asks the person to map anything.

const MONTHS = { jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5, jul: 6, aug: 7, sep: 8, sept: 8, oct: 9, nov: 10, dec: 11 };
const MONTH_RE = "(jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:t(?:ember)?)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)";

function monthIndex(name) {
  const k = String(name).toLowerCase();
  return k.slice(0, 4) in MONTHS ? MONTHS[k.slice(0, 4)] : MONTHS[k.slice(0, 3)];
}

const pad = (n) => String(n).padStart(2, "0");
const iso = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;

function validYMD(y, m, d) {
  if (y < 1990 || y > 2100 || m < 0 || m > 11 || d < 1 || d > 31) return false;
  const dt = new Date(Date.UTC(y, m, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m && dt.getUTCDate() === d;
}

function fullYear(y) {
  if (y >= 100) return y;
  return y < 70 ? 2000 + y : 1900 + y;
}

// order: "dmy" | "mdy". Only matters for all-numeric dates like 04/05/2024.
export function parseAnyDate(val, { order = "mdy", allowSerial = false, defaultYear = null } = {}) {
  if (val === undefined || val === null || val === "") return null;
  if (val instanceof Date) return isNaN(val) ? null : iso(val.getFullYear(), val.getMonth(), val.getDate());
  if (typeof val === "number") {
    // Excel serial date. UTC getters on purpose — local getters shift the day
    // back by one anywhere west of Greenwich.
    if (!allowSerial || val < 20000 || val > 70000) return null;
    const d = new Date(Math.round((val - 25569) * 86400 * 1000));
    return iso(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  }
  const s = String(val).trim();
  if (!s) return null;

  let m;
  // 2024-03-05, 2024/03/05, 2024-03-05T10:00:00
  if ((m = s.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})(?:[T\s].*)?$/))) {
    const [y, mo, d] = [+m[1], +m[2] - 1, +m[3]];
    return validYMD(y, mo, d) ? iso(y, mo, d) : null;
  }
  // 05/03/2024, 5-3-24, 05.03.2024 (optionally followed by a time)
  if ((m = s.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2,4})(?:[T\s,].*)?$/))) {
    let a = +m[1], b = +m[2];
    const y = fullYear(+m[3]);
    let d, mo;
    if (a > 12) { d = a; mo = b - 1; }
    else if (b > 12) { mo = a - 1; d = b; }
    else if (order === "dmy") { d = a; mo = b - 1; }
    else { mo = a - 1; d = b; }
    return validYMD(y, mo, d) ? iso(y, mo, d) : null;
  }
  // 5 Mar 2024, 05-Mar-24, 5th March 2024
  if ((m = s.match(new RegExp(`^(\\d{1,2})(?:st|nd|rd|th)?[\\s\\-/.,]*${MONTH_RE}[a-z]*[\\s\\-/.,]*(\\d{2,4})(?:[T\\s,].*)?$`, "i")))) {
    const d = +m[1], mo = monthIndex(m[2]), y = fullYear(+m[3]);
    return validYMD(y, mo, d) ? iso(y, mo, d) : null;
  }
  // Mar 5, 2024 / March 5 2024
  if ((m = s.match(new RegExp(`^${MONTH_RE}[a-z]*\\.?[\\s\\-/.,]*(\\d{1,2})(?:st|nd|rd|th)?[\\s\\-/.,]+(\\d{2,4})(?:[T\\s,].*)?$`, "i")))) {
    const mo = monthIndex(m[1]);
    const d = +m[2], y = fullYear(+m[3]);
    return validYMD(y, mo, d) ? iso(y, mo, d) : null;
  }
  // No year: "Mar 5", "5 Mar", "03/05" — only if the caller knows the year
  if (defaultYear) {
    if ((m = s.match(new RegExp(`^${MONTH_RE}[a-z]*\\.?\\s+(\\d{1,2})$`, "i")))) {
      const mo = monthIndex(m[1]);
      return validYMD(defaultYear, mo, +m[2]) ? iso(defaultYear, mo, +m[2]) : null;
    }
    if ((m = s.match(new RegExp(`^(\\d{1,2})\\s+${MONTH_RE}[a-z]*$`, "i")))) {
      const mo = monthIndex(m[2]);
      return validYMD(defaultYear, mo, +m[1]) ? iso(defaultYear, mo, +m[1]) : null;
    }
    if ((m = s.match(/^(\d{1,2})[/-](\d{1,2})$/))) {
      let a = +m[1], b = +m[2], d, mo;
      if (a > 12) { d = a; mo = b - 1; } else if (b > 12) { mo = a - 1; d = b; }
      else if (order === "dmy") { d = a; mo = b - 1; } else { mo = a - 1; d = b; }
      return validYMD(defaultYear, mo, d) ? iso(defaultYear, mo, d) : null;
    }
  }
  return null;
}

// Looks at every all-numeric date in a column and works out day-first vs
// month-first from the data itself; only falls back to the device locale when
// every date in the file is genuinely ambiguous.
export function detectDateOrder(samples, localeHint = "en-US") {
  let dayFirst = 0, monthFirst = 0;
  for (const v of samples) {
    const m = typeof v === "string" && v.trim().match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2,4})/);
    if (!m) continue;
    if (+m[1] > 12 && +m[2] <= 12) dayFirst++;
    else if (+m[2] > 12 && +m[1] <= 12) monthFirst++;
  }
  if (dayFirst > monthFirst) return "dmy";
  if (monthFirst > dayFirst) return "mdy";
  const loc = String(localeHint || "en-US");
  return /^en-US$|^en$|^es-US/i.test(loc) || /^fil|^en-PH/i.test(loc) ? "mdy" : "dmy";
}

const CURRENCY_WORDS = /\b(?:USD|EUR|GBP|INR|AUD|CAD|SGD|AED|JPY|RS|INR)\b\.?/gi;

export function parseAmount(val) {
  if (val === undefined || val === null || val === "") return null;
  if (typeof val === "number") return isFinite(val) ? val : null;
  let s = String(val).trim();
  if (!s) return null;
  if (/^\d{1,4}[-/]\d{1,2}[-/]\d{1,4}(?:[T\s].*)?$/.test(s)) return null; // a date, not money
  let neg = false;
  if (/^\(.*\)$/.test(s)) { neg = true; s = s.slice(1, -1); }
  const dr = /\bDR\b|\bDEBIT\b/i.test(s) && !/[a-z]{3,}/i.test(s.replace(/\bDEBIT\b/i, ""));
  const cr = /\bCR\b|\bCREDIT\b/i.test(s) && !/[a-z]{3,}/i.test(s.replace(/\bCREDIT\b/i, ""));
  s = s.replace(/\b(?:CR|DR|DEBIT|CREDIT)\b/gi, "").replace(CURRENCY_WORDS, "");
  if (/[a-z]/i.test(s)) return null; // letters left over → not an amount
  if (/^\s*[-\u2212\u2013]/.test(s)) neg = !neg ? true : neg;
  if (/[-\u2212\u2013]\s*$/.test(s)) neg = true;
  s = s.replace(/[$€£₹¥\s'\u00A0]/g, "").replace(/[-\u2212\u2013+]/g, "");
  if (!/\d/.test(s)) return null;
  if (/[^0-9.,]/.test(s)) return null;
  const lastDot = s.lastIndexOf("."), lastComma = s.lastIndexOf(",");
  if (lastDot !== -1 && lastComma !== -1) {
    if (lastComma > lastDot) s = s.replace(/\./g, "").replace(",", "."); // 1.234,56
    else s = s.replace(/,/g, ""); // 1,234.56
  } else if (lastComma !== -1) {
    // 12,50 → decimal comma; 1,250 or 1,250,000 → thousands
    if (/,\d{2}$/.test(s) && (s.match(/,/g) || []).length === 1) s = s.replace(",", ".");
    else s = s.replace(/,/g, "");
  } else if ((s.match(/\./g) || []).length > 1) {
    s = s.replace(/\./g, "");
  }
  const n = parseFloat(s);
  if (!isFinite(n)) return null;
  if (dr) neg = true;
  if (cr) neg = false;
  return neg ? -n : n;
}

// ---------------------------------------------------------------------------
// Merchant cleaning
// ---------------------------------------------------------------------------

const LEADING_NOISE = [
  /^(?:pos|ach|eft|dda|pending|recurring|checkcard|check card|chkcard|debit card purchase|debit card|debit purchase|debit|card purchase|card payment|purchase authorized on|purchase|visa|mastercard|online payment to|online payment|online pmt|online|payment to|pmt to|withdrawal|wdl|bill pay|billpay|zelle payment to|zelle to|zelle from|point of sale)\b[\s:*-]*/i,
  /^(?:sq|tst|pp|paypal|pay|sp|ic|dd|dnh|fsp|gglpay|google pay|apple pay)\s*\*\s*/i,
  /^\d{2}\/\d{2}\s+/, // "03/05 STARBUCKS"
];
const TRAILING_NOISE = [
  /\s+(?:x{2,}|\*{2,})\d{2,6}\b.*$/i,           // XXXX1234 / ****1234
  /\s+card\s*(?:ending|no\.?|#)?\s*\d{3,6}\b.*$/i,
  /\s+#\s*\d+\b.*$/,                            // store #123
  /\s+\d{1,2}\/\d{1,2}(?:\/\d{2,4})?\b.*$/,     // trailing date
  /\s+(?:ref|id|txn|trace|auth)\s*[:#]?\s*[\w-]{4,}.*$/i,
  /\s+\d{5,}.*$/,                               // long digit runs
  /\s?\*\s?[A-Za-z0-9]*\d[A-Za-z0-9]*$/,           // *2K4MN reference tails
  /\s+\d{3,4}$/,                                  // store number
  /\s+[A-Z]{2}\s*\d{5}(?:-\d{4})?$/,            // ST 70112
];

export function cleanMerchant(raw) {
  let s = String(raw ?? "").replace(/\s+/g, " ").trim();
  if (!s) return "";
  for (let i = 0; i < 3; i++) {
    const before = s;
    for (const re of LEADING_NOISE) s = s.replace(re, "");
    if (s === before) break;
  }
  for (const re of TRAILING_NOISE) s = s.replace(re, "");
  // "STARBUCKS NEW ORLEANS LA" → drop a trailing 2-letter state when the line is shouty
  const letters = s.replace(/[^A-Za-z]/g, "");
  const upperRatio = letters ? letters.replace(/[^A-Z]/g, "").length / letters.length : 0;
  if (upperRatio > 0.7 && s.split(" ").length >= 3) s = s.replace(/\s+[A-Z]{2}$/, "");
  s = s.replace(/[*\s]+$/g, "").replace(/^[*\s-]+/, "").trim();
  if (!s) s = String(raw).trim();
  if (upperRatio > 0.7) s = s.toLowerCase().replace(/(^|[\s&/(-])([a-z])/g, (_, a, b) => a + b.toUpperCase());
  return s.length > 60 ? s.slice(0, 60).trim() : s;
}

export function merchantKey(raw) {
  return cleanMerchant(raw).toLowerCase().replace(/[^a-z\s]/g, " ").split(/\s+/).filter(Boolean).slice(0, 2).join(" ");
}

// ---------------------------------------------------------------------------
// Table reading
// ---------------------------------------------------------------------------

const H = {
  date: /date|posted|posting|\bday\b|\bwhen\b|\btime\b/i,
  dateBad: /value date|settle|maturity|due|statement|cleared/i,
  amount: /amount|amt|value|total|price|cost|sum|spent|spend/i,
  debit: /debit|withdraw|paid out|money out|outflow|\bdr\b|expense|spent/i,
  credit: /credit|deposit|paid in|money in|inflow|\bcr\b|income|received/i,
  balance: /balance|running|\bbal\b|available/i,
  idLike: /\bref|reference|cheque|check\s?(?:no|num|#)|number|\bno\b\.?$|\bid\b|account|card|serial|\bseq|memo\s?no/i,
  desc: /descri|detail|narrat|merchant|payee|memo|particular|name|note|transaction|vendor|store|title|remarks?/i,
  descBad: /type|code|category|status/i,
  category: /categor|class|tag|label|group|bucket/i,
  type: /^(?:type|dr\/cr|cr\/dr|debit\/credit|credit\/debit|transaction type|txn type|direction)$/i,
};

const isBlank = (v) => v === undefined || v === null || String(v).trim() === "";
const asText = (v) => (v === undefined || v === null ? "" : String(v).trim());

function rowCellKinds(row, order) {
  let text = 0, num = 0, date = 0;
  row.forEach((c) => {
    if (isBlank(c)) return;
    if (parseAnyDate(c, { order }) !== null) date++;
    else if (parseAmount(c) !== null && typeof c !== "string") num++;
    else if (parseAmount(c) !== null && /^[\s$€£₹¥(-]*[\d.,]+\)?\s*$/.test(String(c))) num++;
    else text++;
  });
  return { text, num, date };
}

// Picks the real header row (skipping title rows, bank logos, blank spacers) or
// decides the sheet has none at all.
function findHeader(rows, order) {
  let best = -1, bestScore = 0;
  const limit = Math.min(rows.length - 1, 25);
  for (let i = 0; i < limit; i++) {
    const row = rows[i] || [];
    const filled = row.filter((c) => !isBlank(c)).length;
    if (filled < 2) continue;
    const k = rowCellKinds(row, order);
    if (k.text < 2 || k.text < (k.num + k.date)) continue;
    const hints = row.filter((c) => !isBlank(c) && (H.date.test(c) || H.amount.test(c) || H.desc.test(c) || H.debit.test(c) || H.credit.test(c) || H.category.test(c))).length;
    const score = hints * 10 + k.text * 2 + filled;
    if (score > bestScore) { bestScore = score; best = i; }
  }
  return best; // -1 → headerless
}

function sampleCol(rows, c, n = 400) {
  const out = [];
  for (let i = 0; i < rows.length && out.length < n; i++) if (!isBlank(rows[i][c])) out.push(rows[i][c]);
  return out;
}

function analyseTable(rows, localeHint) {
  const width = Math.max(...rows.slice(0, 200).map((r) => r.length), 0);
  const norm = rows.map((r) => Array.from({ length: width }, (_, i) => (r[i] === undefined ? "" : r[i])));
  if (norm.length < 2 || width < 2) return null;

  // date order from the whole table, before we even know which column is the date
  const allStrings = [];
  norm.slice(0, 300).forEach((r) => r.forEach((c) => { if (typeof c === "string") allStrings.push(c); }));
  const order = detectDateOrder(allStrings, localeHint);

  const headerIdx = findHeader(norm, order);
  const headers = headerIdx >= 0 ? norm[headerIdx].map((h, i) => asText(h) || `Column ${i + 1}`) : norm[0].map((_, i) => `Column ${i + 1}`);
  const body = norm.slice(headerIdx + 1).filter((r) => r.some((c) => !isBlank(c)));
  if (body.length === 0) return null;

  // Profile each column by its content
  const cols = headers.map((h, c) => {
    const vals = sampleCol(body, c);
    const n = vals.length || 1;
    let dates = 0, serialDates = 0, nums = 0, decimals = 0, bigInts = 0, pos = 0, neg = 0, textLen = 0;
    const uniq = new Set();
    vals.forEach((v) => {
      const d = parseAnyDate(v, { order });
      if (d) { dates++; return; }
      if (typeof v === "number" && parseAnyDate(v, { order, allowSerial: true })) serialDates++;
      const a = parseAmount(v);
      if (a !== null && !(typeof v === "string" && /[a-z]{2,}/i.test(v))) {
        nums++;
        if (a > 0) pos++; else if (a < 0) neg++;
        if (/[.,]\d{1,2}\s*\)?\s*$/.test(String(v)) || (typeof v === "number" && !Number.isInteger(v))) decimals++;
        if (Math.abs(a) >= 100000 && Number.isInteger(a)) bigInts++;
      } else { textLen += String(v).length; }
      uniq.add(String(v).toLowerCase());
    });
    const textCount = n - dates - nums;
    return {
      c, h, n: vals.length,
      fill: vals.length / body.length,
      dateRatio: dates / n, serialRatio: serialDates / n, numRatio: nums / n,
      decRatio: nums ? decimals / nums : 0, bigIntRatio: nums ? bigInts / nums : 0,
      pos, neg, textRatio: textCount / n,
      avgLen: textCount ? textLen / textCount : 0,
      uniq: uniq.size, uniqRatio: uniq.size / n,
    };
  });

  const used = new Set();
  const take = (col) => { if (col) used.add(col.c); return col; };

  // Balance columns are never the spend
  cols.forEach((col) => { if (H.balance.test(col.h)) used.add(col.c); });

  // Date column
  let dateCol = null;
  {
    const cand = cols.filter((c) => !used.has(c.c) && (c.dateRatio >= 0.6 || (c.serialRatio >= 0.8 && (H.date.test(c.h) || headerIdx < 0))));
    cand.sort((a, b) => {
      const sa = (a.dateRatio + a.serialRatio) * 5 + (H.date.test(a.h) ? 3 : 0) - (H.dateBad.test(a.h) ? 4 : 0) + a.fill - a.c * 0.01;
      const sb = (b.dateRatio + b.serialRatio) * 5 + (H.date.test(b.h) ? 3 : 0) - (H.dateBad.test(b.h) ? 4 : 0) + b.fill - b.c * 0.01;
      return sb - sa;
    });
    dateCol = take(cand[0] || null);
  }
  const serialDates = !!(dateCol && dateCol.serialRatio > dateCol.dateRatio);

  // Direction column (DR/CR text)
  let typeCol = null;
  {
    const DIRWORDS = /^(?:debit|credit|dr|cr|withdrawal|deposit|expense|income|out|in|d|c)$/i;
    const cand = cols.filter((c) => !used.has(c.c) && c.textRatio > 0.8 && c.uniq <= 12).filter((c) => {
      const vals = sampleCol(body, c.c, 60);
      return vals.length && vals.filter((v) => DIRWORDS.test(asText(v))).length / vals.length > 0.7;
    });
    typeCol = take(cand[0] || null);
  }

  // Amount columns
  const numeric = cols.filter((c) => !used.has(c.c) && c.numRatio >= 0.35 && !H.idLike.test(c.h) && !(c.bigIntRatio > 0.8 && c.decRatio < 0.2));
  let debitCol = numeric.find((c) => H.debit.test(c.h) && !H.credit.test(c.h)) || null;
  let creditCol = numeric.find((c) => H.credit.test(c.h) && !H.debit.test(c.h)) || null;
  let amountCol = null;
  if (!(debitCol && creditCol)) {
    const single = numeric.filter((c) => c !== debitCol && c !== creditCol);
    single.sort((a, b) => {
      const sa = (H.amount.test(a.h) ? 4 : 0) + a.decRatio * 2 + a.numRatio * 2 + a.fill - a.c * 0.01;
      const sb = (H.amount.test(b.h) ? 4 : 0) + b.decRatio * 2 + b.numRatio * 2 + b.fill - b.c * 0.01;
      return sb - sa;
    });
    amountCol = single[0] || null;
    // A lone "Debit" column with no credit counterpart is just an amount column
    if (!amountCol && debitCol) { amountCol = debitCol; debitCol = null; }
    if (amountCol && (debitCol || creditCol)) {
      // mixed: one of debit/credit plus a generic amount → trust the generic one
      if (!(debitCol && creditCol)) { debitCol = null; creditCol = null; }
    }
  }
  if (debitCol && creditCol) { take(debitCol); take(creditCol); amountCol = null; }
  else if (amountCol) take(amountCol);
  if (!dateCol || !(amountCol || (debitCol && creditCol))) return { failed: true, headers, reason: !dateCol ? "date" : "amount" };

  // Description (+ spare text columns used only for matching)
  const textCols = cols.filter((c) => !used.has(c.c) && c.textRatio >= 0.5 && c.n > 0);
  textCols.sort((a, b) => {
    const sa = (H.desc.test(a.h) ? 6 : 0) - (H.descBad.test(a.h) && !H.desc.test(a.h) ? 3 : 0) + Math.min(a.avgLen, 40) / 10 + a.uniqRatio * 2 + a.fill;
    const sb = (H.desc.test(b.h) ? 6 : 0) - (H.descBad.test(b.h) && !H.desc.test(b.h) ? 3 : 0) + Math.min(b.avgLen, 40) / 10 + b.uniqRatio * 2 + b.fill;
    return sb - sa;
  });
  const descCol = take(textCols[0] || null);

  // Category column supplied by the file itself (Chase, Mint, Monzo, budget sheets…)
  let catCol = null;
  {
    const cand = cols.filter((c) => !used.has(c.c) && c.textRatio >= 0.6 && c.uniq >= 2 && c.uniq <= Math.max(25, body.length * 0.3) && H.category.test(c.h));
    catCol = take(cand[0] || null);
  }
  const extraTextCols = cols.filter((c) => !used.has(c.c) && c.textRatio >= 0.5 && c.avgLen >= 4).slice(0, 2);

  return {
    failed: false, order, headers, headerIdx, body, serialDates,
    dateCol, amountCol, debitCol, creditCol, typeCol, descCol, catCol, extraTextCols,
  };
}

// Decides which sign means "money out" for a single signed amount column.
function spendingSign(values, descs) {
  let neg = 0, pos = 0;
  values.forEach((v) => { if (v < 0) neg++; else if (v > 0) pos++; });
  if (neg === 0) return 1;   // everything positive → all spending (card export / expense sheet)
  if (pos === 0) return -1;  // everything negative → all spending
  // mixed: whichever sign is the clear majority is spending; ties → look at descriptions
  if (neg > pos * 1.15) return -1;
  if (pos > neg * 1.15) return 1;
  let incomeNeg = 0, incomePos = 0;
  values.forEach((v, i) => { if (INCOME_RE.test(descs[i] || "")) { if (v < 0) incomeNeg++; else incomePos++; } });
  return incomePos >= incomeNeg ? -1 : 1;
}

const INCOME_RE = /\b(?:salary|payroll|direct dep|deposit|dividend|interest paid|interest earned|refund|reversal|cashback|cash back|rebate|reimburse|credit interest|tax refund|income|paycheck)\b/i;
const TRANSFER_RE = /\b(?:transfer|xfer|trf|payment thank you|payment - thank you|autopay|auto pay|online pmt|credit card payment|card payment|payment received|cc payment|crd pmt|mobile payment|e-?payment|loan payment|to savings|from savings|own account|between accounts)\b/i;
const SUMMARY_RE = /^(?:total|subtotal|grand total|balance|opening|closing|brought forward|carried forward|previous balance|new balance|statement|summary|beginning|ending|page \d)/i;

function tableToTransactions(info, sheetName) {
  const { body, dateCol, amountCol, debitCol, creditCol, typeCol, descCol, catCol, extraTextCols, order, serialDates } = info;
  const raw = [];
  body.forEach((r, i) => {
    const date = parseAnyDate(r[dateCol.c], { order, allowSerial: serialDates });
    const desc = descCol ? asText(r[descCol.c]) : "";
    const extra = extraTextCols.map((c) => asText(r[c.c])).filter(Boolean).join(" ");
    const fileCategory = catCol ? asText(r[catCol.c]) : "";
    let signed = null;
    if (debitCol && creditCol) {
      const d = parseAmount(r[debitCol.c]), c = parseAmount(r[creditCol.c]);
      if (d && Math.abs(d) > 0) signed = -Math.abs(d);
      else if (c && Math.abs(c) > 0) signed = Math.abs(c);
    } else {
      signed = parseAmount(r[amountCol.c]);
      if (signed !== null && typeCol) {
        const t = asText(r[typeCol.c]).toLowerCase();
        if (/^(?:credit|cr|deposit|income|refund|in|c)$/.test(t)) signed = Math.abs(signed);
        else if (/^(?:debit|dr|withdrawal|expense|out|d)$/.test(t)) signed = -Math.abs(signed);
      }
    }
    if (!date || signed === null || signed === 0) return;
    if (SUMMARY_RE.test(desc)) return;
    raw.push({ date, signed, desc, extra, fileCategory, sheet: sheetName, typed: !!typeCol || !!(debitCol && creditCol), line: i });
  });
  return raw;
}

// ---------------------------------------------------------------------------
// Free text (PDF statements, pasted bank pages)
// ---------------------------------------------------------------------------

const DATE_TOKENS = [
  /\b\d{4}[-/.]\d{1,2}[-/.]\d{1,2}\b/,
  /\b\d{1,2}[-/.]\d{1,2}[-/.]\d{2,4}\b/,
  new RegExp(`\\b\\d{1,2}(?:st|nd|rd|th)?[\\s\\-/.]+${MONTH_RE}[a-z]*\\.?(?:[\\s\\-/.,]+\\d{2,4}(?!\\d)(?![\\s\\-/.]+${MONTH_RE}))?\\b`, "i"),
  new RegExp(`\\b${MONTH_RE}[a-z]*\\.?\\s+\\d{1,2}(?:st|nd|rd|th)?(?:,?\\s+\\d{2,4})?\\b`, "i"),
  /\b\d{1,2}[/-]\d{1,2}\b(?![/-]?\d)/,
];
const MONEY_TOKEN = /\(?[-\u2212]?\s?[$€£₹¥]?\s?\d{1,3}(?:[,.\u00A0 ]\d{3})*[.,]\d{2}\)?(?:\s?-|\s?(?:CR|DR)\b)?|\(?[-\u2212]?\s?[$€£₹¥]\s?\d+\)?/gi;

// ---- Statement date -------------------------------------------------------
// Card/bank PDFs usually print transactions as "Dec 28" with no year. The year
// belongs to the *statement* (its closing date / period end), so we find that
// date from the header and borrow its year — rolling back a year for months
// that come after the statement month (a "Dec 28" line on a January statement).
const STMT_LABEL = /statement|closing|billing|period|cycle|through|thru|ending|as of/i;
const STMT_NOT = /payment due|due date|minimum payment|pay by/i;
const FULL_DATE_TOKENS = [
  /\b\d{4}[-/.]\d{1,2}[-/.]\d{1,2}\b/g,
  /\b\d{1,2}[-/.]\d{1,2}[-/.]\d{2,4}\b/g,
  new RegExp(`\\b\\d{1,2}(?:st|nd|rd|th)?[\\s\\-/.]+${MONTH_RE}[a-z]*\\.?,?[\\s\\-/.]+\\d{4}\\b`, "gi"),
  new RegExp(`\\b${MONTH_RE}[a-z]*\\.?\\s+\\d{1,2}(?:st|nd|rd|th)?,?\\s+\\d{4}\\b`, "gi"),
];

function fullDatesIn(line, order) {
  const out = [];
  for (const re of FULL_DATE_TOKENS) {
    for (const m of line.matchAll(re)) {
      const d = parseAnyDate(m[0], { order });
      if (d) out.push(d);
    }
  }
  return out;
}

function stmtFromISO(d, monthKnown = true) {
  return { iso: d, year: +d.slice(0, 4), month: +d.slice(5, 7) - 1, day: +d.slice(8, 10), monthKnown };
}

export function detectStatementDate(lines, order = "mdy") {
  const found = [];
  const limit = Math.min(lines.length, 120); // headers live at the top
  for (let i = 0; i < limit; i++) {
    const l = lines[i];
    if (!STMT_LABEL.test(l) || STMT_NOT.test(l)) continue;
    let ds = fullDatesIn(l, order);
    // label and value sometimes land on separate lines in a PDF
    if (!ds.length && i + 1 < lines.length && !STMT_NOT.test(lines[i + 1])) ds = fullDatesIn(lines[i + 1], order);
    found.push(...ds);
  }
  if (found.length) return stmtFromISO(found.sort()[found.length - 1]); // latest = period end
  // No labelled date: fall back to the first year printed in the header
  const head = lines.slice(0, 60).join(" ").match(/\b(20\d{2})\b/);
  return head ? { iso: null, year: +head[1], month: 11, day: 31, monthKnown: false } : null;
}

function parseTextCore(text, localeHint, { statementDate = null, force = false } = {}) {
  const lines = String(text || "").split(/\r?\n/).map((l) => l.replace(/\s+/g, " ").trim()).filter(Boolean);
  const dateStrings = [];
  lines.forEach((l) => { const m = l.match(DATE_TOKENS[1]); if (m) dateStrings.push(m[0]); });
  const order = detectDateOrder(dateStrings, localeHint);

  const stmt = statementDate && /^\d{4}-\d{2}-\d{2}$/.test(statementDate)
    ? stmtFromISO(statementDate)
    : detectStatementDate(lines, order);
  const fallbackYear = stmt ? stmt.year : new Date().getFullYear();

  // Month/day come from the transaction; the year comes from the statement.
  const resolveDate = (token) => {
    const withYear = parseAnyDate(token, { order, defaultYear: fallbackYear });
    if (!withYear) return null;
    const explicit = parseAnyDate(token, { order }) !== null;
    if (!stmt || !stmt.monthKnown) return withYear;
    if (explicit && !force) return withYear; // a printed full date is trusted unless the user pinned the statement date
    const mo = +withYear.slice(5, 7) - 1, d = +withYear.slice(8, 10);
    const y = mo > stmt.month ? stmt.year - 1 : stmt.year;
    return validYMD(y, mo, d) ? iso(y, mo, d) : withYear;
  };

  const parsed = [];
  lines.forEach((line, idx) => {
    // Earliest token wins: on "Dec 28 Dec 29 SHOP" the "28 Dec 29" pattern must not beat "Dec 28"
    let dm = null;
    for (const re of DATE_TOKENS) {
      const m = line.match(re);
      if (m && m.index <= Math.max(12, line.length * 0.25) && (!dm || m.index < dm.index || (m.index === dm.index && m[0].length > dm[0].length))) dm = m;
    }
    if (!dm) return;
    const date = resolveDate(dm[0]);
    if (!date) return;
    let rest = (line.slice(0, dm.index) + " " + line.slice(dm.index + dm[0].length)).trim();
    // Some statements print a posting date right after the transaction date
    for (const re of DATE_TOKENS) {
      const m2 = rest.match(re);
      if (m2 && m2.index <= 3 && resolveDate(m2[0])) { rest = rest.slice(m2.index + m2[0].length).trim(); break; }
    }
    const tokens = [...rest.matchAll(MONEY_TOKEN)].map((m) => ({ s: m[0], i: m.index }));
    if (tokens.length === 0) return;
    parsed.push({ date, rest, tokens, idx });
  });
  if (parsed.length === 0) return { raw: [], stmt };

  const withBalance = parsed.filter((p) => p.tokens.length >= 2).length / parsed.length > 0.6;
  const raw = [];
  parsed.forEach((p) => {
    const tok = withBalance && p.tokens.length >= 2 ? p.tokens[p.tokens.length - 2] : p.tokens[p.tokens.length - 1];
    const signed = parseAmount(tok.s);
    if (signed === null || signed === 0) return;
    const desc = p.rest.slice(0, p.tokens[0].i).trim() || p.rest.replace(MONEY_TOKEN, "").trim();
    if (SUMMARY_RE.test(desc) || !desc) return;
    raw.push({ date: p.date, signed, desc, extra: "", fileCategory: "", sheet: "", typed: false, line: p.idx });
  });
  return { raw, stmt };
}

export function parseTextStatement(text, localeHint, opts) {
  return parseTextCore(text, localeHint, opts).raw;
}

// ---------------------------------------------------------------------------
// Public entry points
// ---------------------------------------------------------------------------

function finalise(raw, info) {
  const signs = raw.map((r) => r.signed);
  const typedOnly = raw.length > 0 && raw.every((r) => r.typed);
  const sign = typedOnly ? -1 : spendingSign(signs, raw.map((r) => r.desc));
  const out = raw.map((r, i) => {
    const isSpend = r.signed * sign > 0;
    const text = `${r.desc} ${r.extra}`;
    let kind = isSpend ? "expense" : "income";
    let reason = "";
    if (kind === "expense" && TRANSFER_RE.test(r.desc)) { kind = "transfer"; reason = "Looks like a transfer or card payment"; }
    else if (kind === "income" && TRANSFER_RE.test(r.desc)) { kind = "transfer"; reason = "Looks like a transfer or card payment"; }
    else if (kind === "income") reason = INCOME_RE.test(text) ? "Deposit or income" : (typedOnly ? "Money in" : "Money in (deposit or refund)");
    return {
      id: `r${i}`,
      date: r.date,
      amount: Math.round(Math.abs(r.signed) * 100) / 100,
      raw: r.desc,
      merchant: cleanMerchant(r.desc) || (r.fileCategory || "Unlabelled"),
      // the full statement reference, kept for the saved entry's note
      note: String(r.desc || "").replace(/\s+/g, " ").trim() || cleanMerchant(r.desc) || (r.fileCategory || "Unlabelled"),
      matchText: text,
      fileCategory: r.fileCategory,
      kind, reason, sheet: r.sheet,
    };
  });
  return { rows: out, sign, info };
}

// tables: [{ name, rows: any[][] }]
export function readTables(tables, { localeHint } = {}) {
  let all = [];
  const notes = [];
  let lastInfo = null;
  let failedReason = null;
  for (const t of tables) {
    if (!t.rows || t.rows.length < 2) continue;
    const info = analyseTable(t.rows, localeHint);
    if (!info) continue;
    if (info.failed) { failedReason = failedReason || info.reason; continue; }
    const raw = tableToTransactions(info, t.name);
    if (raw.length) { all = all.concat(raw); lastInfo = info; notes.push({ sheet: t.name, rows: raw.length }); }
  }
  if (all.length === 0) return { rows: [], detection: null, failed: true, reason: failedReason };
  const res = finalise(all, lastInfo);
  const i = lastInfo;
  res.detection = {
    source: "table",
    sheets: notes.length,
    date: i.dateCol.h,
    amount: i.debitCol && i.creditCol ? `${i.debitCol.h} / ${i.creditCol.h}` : i.amountCol.h,
    description: i.descCol ? i.descCol.h : null,
    category: i.catCol ? i.catCol.h : null,
    dateOrder: i.order,
    spendingIs: i.debitCol && i.creditCol ? "debit column" : res.sign < 0 ? "negative numbers" : "positive numbers",
  };
  return res;
}

// Rows that came from somewhere other than the built-in readers (AI extraction)
export function readRaw(raw) {
  if (!raw.length) return { rows: [], detection: null, failed: true, reason: "ai" };
  const res = finalise(raw.map((r, i) => ({ ...r, extra: "", fileCategory: "", sheet: "", typed: false, line: i })), null);
  res.detection = { source: "ai", sheets: 1, date: "Read by AI", amount: "Read by AI", description: "Read by AI", category: null, dateOrder: null, spendingIs: res.sign < 0 ? "negative numbers" : "positive numbers" };
  return res;
}

export function readText(text, { localeHint, statementDate = null, force = false } = {}) {
  const { raw, stmt } = parseTextCore(text, localeHint, { statementDate, force });
  if (raw.length === 0) return { rows: [], detection: null, failed: true, reason: "text" };
  const res = finalise(raw, null);
  res.detection = {
    source: "text", sheets: 1, date: "Start of each line", amount: "Numbers on each line", description: "Text in between", category: null, dateOrder: null,
    spendingIs: res.sign < 0 ? "negative numbers" : "positive numbers",
    statementDate: stmt && stmt.monthKnown ? stmt.iso : null,
    statementYear: stmt ? stmt.year : null,
  };
  return res;
}

// Flips which sign counts as spending (the one switch we expose, for the rare
// file where the guess is wrong). Operates on already-parsed rows.
export function flipDirection(rows) {
  return rows.map((r) => {
    if (r.kind === "expense") return { ...r, kind: "income", reason: "Money in (deposit or refund)" };
    if (r.kind === "income") return { ...r, kind: TRANSFER_RE.test(r.raw) ? "transfer" : "expense", reason: TRANSFER_RE.test(r.raw) ? "Looks like a transfer or card payment" : "" };
    return r;
  });
}

// Groups rows that look like the same thing every month/week.
export function detectRecurringCosts(rows) {
  const groups = {};
  rows.forEach((r) => {
    const sig = (r.note || r.category || "").trim().toLowerCase() || "misc";
    const key = `${sig}::${Math.round(r.amount)}`;
    (groups[key] = groups[key] || []).push(r);
  });
  const candidates = [];
  Object.values(groups).forEach((group) => {
    if (group.length < 2) return;
    const sorted = group.slice().sort((a, b) => new Date(a.date) - new Date(b.date));
    const months = new Set(sorted.map((r) => r.date.slice(0, 7)));
    if (months.size < 2) return;
    let gap = 0;
    for (let i = 1; i < sorted.length; i++) gap += (new Date(sorted[i].date) - new Date(sorted[i - 1].date)) / 86400000;
    const avg = gap / (sorted.length - 1);
    let frequency = null;
    if (avg >= 25 && avg <= 35) frequency = "monthly";
    else if (avg >= 6 && avg <= 8) frequency = "weekly";
    if (!frequency) return;
    const last = sorted[sorted.length - 1];
    candidates.push({
      key: `${last.category}::${last.note}::${Math.round(last.amount)}`,
      label: last.note || last.category,
      category: last.category, amount: Math.abs(last.amount),
      count: sorted.length, frequency, lastDate: last.date, checked: true,
    });
  });
  return candidates;
}
